/**
 * The wire protocol. Every websocket frame in either direction is one of the
 * members of `ClientMessageSchema` / `ServerMessageSchema`, discriminated on `t`.
 *
 * The server validates every inbound frame with `parseClientMessage` before
 * touching it. The client validates inbound frames in dev and trusts them in
 * production (the server is authoritative anyway).
 *
 * This file is FROZEN after Wave 1. Needed changes go to the orchestrator.
 *
 * Post-freeze additions, each one signed off there: `claim_seat` /
 * `claimable_seats` and the `seat_taken` error code, which are how a phone with
 * no session token gets back into a game it is already in (SPEC §5.4); and
 * `lock_prediction` plus the `pending` list on `prediction_count`, which are how
 * a predictor says "I am done, do not wait for me" and how the room shows who it
 * is still waiting on (SPEC §2.3 phase 2/3).
 */
import { z } from 'zod';
import { MAX_ROUNDS, MIN_ROUNDS } from './constants.js';
import {
  HostTokenSchema,
  LeaderboardSchema,
  PhotoDataUrlSchema,
  PlayerIdSchema,
  PlayerNameSchema,
  PredictionSchema,
  QuestionNumberSchema,
  QuestionSchema,
  RankingHistorySchema,
  RankingSchema,
  RevealPayloadSchema,
  RoomCodeSchema,
  RoomPhaseSchema,
  RoomSettingsSchema,
  RoomSnapshotSchema,
  RosterEntrySchema,
  SessionTokenSchema,
  SuperlativesSchema,
  ThumbsVoteSchema,
  TurnStateSchema,
} from './state.js';

/* ========================================================================== */
/* Client -> Server                                                            */
/* ========================================================================== */

/** Take a seat, or reclaim one with a stored session token (SPEC §5.4). */
export const JoinMessageSchema = z.object({
  t: z.literal('join'),
  roomCode: RoomCodeSchema,
  /** Present on reconnect; the server re-binds the existing seat. */
  sessionToken: SessionTokenSchema.optional(),
  /**
   * SPEC §5.5 — proof that this phone is the one that created the room.
   *
   * `POST /api/rooms` mints it alongside the code and hands it to exactly one
   * device; the room keeps its copy and never sends it to anybody. Replaying it
   * here is what makes the creator the host, rather than whichever phone's
   * socket happened to open first — the creator has a name and a selfie to get
   * through before they join, so on the plain first-in rule the host is usually
   * whoever they invited.
   *
   * A wrong or stale token is not an error: it just does not make anybody host.
   */
  hostToken: HostTokenSchema.optional(),
});

/**
 * The answer to `claimable_seats` (SPEC §5.4).
 *
 * A device with no session token — a private window, a cleared browser, a
 * borrowed phone — is offered the seats whose owners are offline, and this is
 * how it takes one. `playerId` absent means "none of these, I am new", which
 * the server only honours in the lobby.
 *
 * The host token rides along for the same reason it rides on `join`: the
 * creator's phone may be the one that lost its storage, and the seat it takes
 * back is still the host seat (SPEC §5.5).
 */
export const ClaimSeatMessageSchema = z.object({
  t: z.literal('claim_seat'),
  playerId: PlayerIdSchema.optional(),
  hostToken: HostTokenSchema.optional(),
});

/** Name + photo, sent after the consent/camera step. Either field may be sent
 *  alone so the client can save a name before the photo is ready. */
export const SetProfileMessageSchema = z.object({
  t: z.literal('set_profile'),
  name: PlayerNameSchema.optional(),
  photo: PhotoDataUrlSchema.optional(),
});

/** Host only. Number of rounds (SPEC §2.2). */
export const UpdateSettingsMessageSchema = z.object({
  t: z.literal('update_settings'),
  rounds: z.number().int().min(MIN_ROUNDS).max(MAX_ROUNDS).optional(),
  deckId: z.string().min(1).optional(),
});

/** Host only. Randomises turn order and moves to question-select. */
export const StartGameMessageSchema = z.object({
  t: z.literal('start_game'),
});

/** Turn player only. Taps a number on the numbered grid. */
export const SelectQuestionMessageSchema = z.object({
  t: z.literal('select_question'),
  number: QuestionNumberSchema,
});

/** Turn player only. The canonical ranking; fires the 10s guillotine. */
export const SubmitRankingMessageSchema = z.object({
  t: z.literal('submit_ranking'),
  /** All players including the turn player, first place first. */
  ranking: RankingSchema,
});

/**
 * Predictors. Sent on every change; the last one before the lock wins.
 *
 * This fires whenever the pair is *complete*, not only when the player says they
 * are finished — that is the whole reason `lock_prediction` is a separate
 * message. A complete pair scores whether or not its owner ever pressed Submit
 * (SPEC §2.3 phase 3 penalises *partial* picks, not unsubmitted ones), so the
 * server has to already hold the pair by the time the countdown expires.
 */
export const SubmitPredictionMessageSchema = z.object({
  t: z.literal('submit_prediction'),
  top: PlayerIdSchema,
  bottom: PlayerIdSchema,
});

/**
 * Predictors. "I am done — do not wait for me." Final, and one-way.
 *
 * Carries no picks on purpose. The pair is already on record via
 * `submit_prediction`; this message only changes what the *room* may do next.
 * The server rejects it unless a complete pair is already stored for that
 * player, so locking can never freeze a half-answer into place.
 *
 * Two things follow from a lock, and neither is cosmetic:
 *
 * 1. That player is done editing. `prediction_ack.editable` goes false and
 *    further `submit_prediction` frames from them are refused. There is no
 *    unlock — this is the one control in the prediction flow with no way back,
 *    which is why the client only offers it once both slots are full.
 * 2. It feeds the all-in check. When every *connected* predictor has locked, the
 *    room stops waiting: if the ranking is already in, `rank-and-predict` goes
 *    straight to `reveal` with no lock-countdown at all; if the countdown is
 *    already running, it ends immediately.
 *
 * Connected predictors only, deliberately — and not because a dark phone is
 * being written off. Whatever it had on record still scores in full (SPEC §5.4);
 * what it cannot do is pick, change or lock, so the seconds held open for it are
 * seconds nobody can spend. Counting it would let one dead battery reimpose the
 * full 10-second wait on everybody else for the rest of the game, in exchange
 * for an answer that cannot arrive.
 */
export const LockPredictionMessageSchema = z.object({
  t: z.literal('lock_prediction'),
});

/** Anonymous, one per player per question, during justification (SPEC §2.5). */
export const ThumbsVoteMessageSchema = z.object({
  t: z.literal('thumbs_vote'),
  vote: ThumbsVoteSchema,
});

/** Turn player's "Done" button, ending the justification phase. */
export const JustificationDoneMessageSchema = z.object({
  t: z.literal('justification_done'),
});

/** Host only. Force-advance out of justification when the turn player stalls. */
export const ForceAdvanceMessageSchema = z.object({
  t: z.literal('force_advance'),
});

/** Host only. Void a turn whose player has been gone > grace period (SPEC §5.4). */
export const SkipTurnMessageSchema = z.object({
  t: z.literal('skip_turn'),
  playerId: PlayerIdSchema,
});

/** Host only. For the person who actually went home (SPEC §5.4). */
export const RemovePlayerMessageSchema = z.object({
  t: z.literal('remove_player'),
  playerId: PlayerIdSchema,
});

/** Host only. Final leaderboard shows immediately (SPEC §2.2). */
export const EndGameMessageSchema = z.object({
  t: z.literal('end_game'),
});

/** Host only. Same room, same players, same photos; used numbers stay burned. */
export const PlayAgainMessageSchema = z.object({
  t: z.literal('play_again'),
});

/** Keepalive / clock sync. Answered with `pong`. */
export const PingMessageSchema = z.object({
  t: z.literal('ping'),
  /** Client clock at send time, echoed back for RTT estimation. */
  sentAt: z.number().int(),
});

export const ClientMessageSchema = z.discriminatedUnion('t', [
  JoinMessageSchema,
  ClaimSeatMessageSchema,
  SetProfileMessageSchema,
  UpdateSettingsMessageSchema,
  StartGameMessageSchema,
  SelectQuestionMessageSchema,
  SubmitRankingMessageSchema,
  SubmitPredictionMessageSchema,
  LockPredictionMessageSchema,
  ThumbsVoteMessageSchema,
  JustificationDoneMessageSchema,
  ForceAdvanceMessageSchema,
  SkipTurnMessageSchema,
  RemovePlayerMessageSchema,
  EndGameMessageSchema,
  PlayAgainMessageSchema,
  PingMessageSchema,
]);

export type ClientMessage = z.infer<typeof ClientMessageSchema>;
export type ClientMessageType = ClientMessage['t'];
export type ClientMessageOf<T extends ClientMessageType> = Extract<ClientMessage, { t: T }>;

/* ========================================================================== */
/* Server -> Client                                                            */
/* ========================================================================== */

/** First frame after a successful join. Carries the token to persist. */
export const WelcomeMessageSchema = z.object({
  t: z.literal('welcome'),
  selfId: PlayerIdSchema,
  sessionToken: SessionTokenSchema,
  isHost: z.boolean(),
  snapshot: RoomSnapshotSchema,
});

/**
 * Answer to a `join` from a device the room does not recognise, when there are
 * seats sitting empty (SPEC §5.4).
 *
 * The session token in localStorage is what normally makes a reconnect silent,
 * and there are ordinary reasons for a phone not to have one: a private window,
 * a browser that clears on close, iOS evicting storage, a phone that died and
 * came back on a friend's device. Without this, that player is locked out of a
 * game they are sitting in — the room can see their face on the board and will
 * not let them near it.
 *
 * So the room says who is missing and lets them point at themselves. It is not
 * authentication and does not pretend to be: anyone who can reach the room
 * already has its code, which means they are in the room. The check that
 * matters is social, and it is the one the game already runs on.
 *
 * Photos for these seats are pushed as `player_photo` frames alongside, so the
 * picker shows faces rather than a list of names.
 */
export const ClaimableSeatsMessageSchema = z.object({
  t: z.literal('claimable_seats'),
  seats: z.array(z.object({ playerId: PlayerIdSchema, name: PlayerNameSchema })),
  /**
   * Whether "none of these — I'm new" is on the table. False once the game has
   * started, and false in a full lobby: the only way in is an empty seat.
   */
  canJoinAsNew: z.boolean(),
});

/** Full state snapshot. Sent on reconnect and after any change the server
 *  would rather not express as a delta. Always safe to render from. */
export const SnapshotMessageSchema = z.object({
  t: z.literal('snapshot'),
  snapshot: RoomSnapshotSchema,
});

/** Any phase transition (SPEC §2.3). `snapshot` is always current. */
export const PhaseChangedMessageSchema = z.object({
  t: z.literal('phase_changed'),
  phase: RoomPhaseSchema,
  previous: RoomPhaseSchema,
  snapshot: RoomSnapshotSchema,
});

/** Join, leave, rename, disconnect, host change, removal. */
export const RosterMessageSchema = z.object({
  t: z.literal('roster'),
  players: z.array(RosterEntrySchema),
  hostId: PlayerIdSchema.nullable(),
});

/** Photos are pushed separately because they are large (SPEC §6). */
export const PlayerPhotoMessageSchema = z.object({
  t: z.literal('player_photo'),
  playerId: PlayerIdSchema,
  photo: PhotoDataUrlSchema.nullable(),
});

export const SettingsMessageSchema = z.object({
  t: z.literal('settings'),
  settings: RoomSettingsSchema,
  /** Recomputed as players join (SPEC §2.2). */
  estimatedDurationMs: z.number().int().min(0),
  /**
   * The server's authoritative round ceiling: floor(remainingNumbers / players),
   * clamped to [MIN_ROUNDS, MAX_ROUNDS].
   *
   * Explicit rather than client-derived on purpose. Both sides previously computed
   * this independently and disagreed after a play-again: the client divided the full
   * 100-number deck, the server divided only what was left unburned, so the lobby
   * offered more rounds than the server would accept. The server is the authority on
   * how much deck remains; the client must render this and never recompute it.
   */
  maxRounds: z.number().int().min(MIN_ROUNDS).max(MAX_ROUNDS),
});

/** Game start: turn order is fixed here and shown in the lobby. */
export const GameStartedMessageSchema = z.object({
  t: z.literal('game_started'),
  turnOrder: z.array(PlayerIdSchema),
  rounds: z.number().int(),
  turnsTotal: z.number().int(),
});

/** Entering question-select for a new turn. */
export const TurnStartedMessageSchema = z.object({
  t: z.literal('turn_started'),
  turn: TurnStateSchema,
  /** Greyed out on the grid. */
  usedQuestionNumbers: z.array(QuestionNumberSchema),
});

/** The mapped question, revealed to every player simultaneously. */
export const QuestionRevealedMessageSchema = z.object({
  t: z.literal('question_revealed'),
  question: QuestionSchema,
  turnIndex: z.number().int(),
});

/**
 * Who the room is still waiting on. Sent to predictors; never to the turn player.
 *
 * The privacy line here is between the *content* of a prediction and the *fact*
 * that someone is finished, and only the first is secret. What anybody picked is
 * never sent to anybody before the reveal — not the counts per player, not the
 * names, nothing that could be differenced across frames to infer a pick. Who
 * has locked in is a different fact: it is the room looking around to see who is
 * still on their phone, which is exactly what people do at the table anyway.
 *
 * An earlier version of this message went to everyone, and the turn player's
 * ranking screen carried a live "7 of 11 predictions in" footer. That is
 * reversed: this message is now withheld from the turn player entirely. SPEC
 * §2.3 phase 3 already puts the clock in their hands — they submit when they are
 * ready — so the count was not information they could act on, only a reason to
 * hurry or to stall while watching a number.
 *
 * `pending` is the ids of connected predictors who have not locked, so the
 * client can render the waiting room by face rather than as a tally. A predictor
 * who has locked is absent from it; so is a disconnected one, which is what lets
 * the all-in check fire without them (SPEC §5.4 — a disconnected seat scores
 * zero regardless).
 */
export const PredictionCountMessageSchema = z.object({
  t: z.literal('prediction_count'),
  received: z.number().int().min(0),
  expected: z.number().int().min(0),
  /** Connected predictors who have not locked yet. Never the turn player. */
  pending: z.array(PlayerIdSchema),
});

/** Private ack to one predictor that their picks are on record. */
export const PredictionAckMessageSchema = z.object({
  t: z.literal('prediction_ack'),
  prediction: PredictionSchema,
  /**
   * Whether this player may still change their picks.
   *
   * False once the hard lock has fired for the room, and false once *this*
   * player has sent `lock_prediction` — locking is final and personal, so one
   * predictor being done says nothing about anybody else's editability.
   */
  editable: z.boolean(),
});

/**
 * The guillotine. Chime fires on every phone (SPEC §2.3 phase 3).
 *
 * No longer guaranteed to occur. The countdown is a backstop for stragglers, so
 * the room skips it when there are none: if every connected predictor has locked
 * before the ranking arrives, `rank-and-predict` goes straight to `reveal` and
 * this message is never sent. Clients must treat the whole `lock-countdown`
 * phase as optional — including on the reconnect/snapshot path — rather than as
 * a step every turn passes through. On the skip path no chime fires at all; the
 * reveal sting is the only cue.
 */
export const LockCountdownMessageSchema = z.object({
  t: z.literal('lock_countdown'),
  endsAt: z.number().int(),
  durationMs: z.number().int(),
  serverNow: z.number().int(),
});

/** Predictions hard-locked at zero. */
export const PredictionsLockedMessageSchema = z.object({
  t: z.literal('predictions_locked'),
  turnIndex: z.number().int(),
});

export const RevealMessageSchema = z.object({
  t: z.literal('reveal'),
  reveal: RevealPayloadSchema,
});

/** Hard cut: "🗣 Sam is explaining themselves." */
export const JustificationMessageSchema = z.object({
  t: z.literal('justification'),
  turnPlayerId: PlayerIdSchema,
  question: QuestionSchema,
  /**
   * The completed ranking, first place first, shown to every player while the
   * turn player talks (SPEC §5.2).
   *
   * Carried here rather than left to whatever the client kept from `reveal`,
   * because this message is also what a reconnecting client receives mid-
   * justification: without it, the person whose phone died comes back to a
   * screen that names an argument they cannot see the subject of.
   */
  ranking: RankingSchema,
  /** True once this client has already voted on this question. */
  selfVoted: z.boolean(),
});

export const RoundLeaderboardMessageSchema = z.object({
  t: z.literal('round_leaderboard'),
  leaderboard: LeaderboardSchema,
  /** Null after the last round. */
  nextRound: z.number().int().nullable(),
});

export const SuperlativesMessageSchema = z.object({
  t: z.literal('superlatives'),
  leaderboard: LeaderboardSchema,
  superlatives: SuperlativesSchema,
  /**
   * Every completed ranking of the game, oldest first, for the post-game review
   * surface (SPEC §2.6).
   *
   * Sent on this message rather than as a message of its own because this is
   * already the game-end payload *and* is exactly what the server replays to a
   * client that reconnects in `superlatives` or `ended` — so the history
   * survives a reload for free, with no second code path to keep in sync.
   *
   * The client holds this in memory only; it is never written to storage
   * (SPEC §6).
   */
  history: RankingHistorySchema,
});

/** Terminal. `reason` distinguishes a natural end from the host's early exit. */
export const GameEndedMessageSchema = z.object({
  t: z.literal('game_ended'),
  reason: z.enum(['completed', 'host_ended', 'abandoned']),
});

/** Same room, same photos, scores reset, used numbers still burned. */
export const GameResetMessageSchema = z.object({
  t: z.literal('game_reset'),
  snapshot: RoomSnapshotSchema,
  /**
   * True when the grid was wiped because too few numbers remained for the next
   * game (SPEC §2.6). Explicit rather than inferred from usedQuestionNumbers
   * emptying, so the client can show the "deck reshuffled" beat deliberately
   * instead of guessing from a state transition it might miss on reconnect.
   */
  deckReshuffled: z.boolean(),
});

/** A turn was voided by the host (SPEC §5.4). No scoring. */
export const TurnSkippedMessageSchema = z.object({
  t: z.literal('turn_skipped'),
  turnIndex: z.number().int(),
  turnPlayerId: PlayerIdSchema,
});

/**
 * Offered to the host once the turn player has been gone longer than the
 * 30-second grace period.
 */
export const SkipOfferedMessageSchema = z.object({
  t: z.literal('skip_offered'),
  turnPlayerId: PlayerIdSchema,
});

export const ErrorCodeSchema = z.enum([
  'room_not_found',
  'room_full',
  'game_in_progress',
  /** The offline seat someone pointed at was taken while they were deciding. */
  'seat_taken',
  'not_host',
  'not_turn_player',
  'wrong_phase',
  'invalid_message',
  'invalid_name',
  'invalid_photo',
  'question_taken',
  'too_few_players',
  'already_voted',
  'rate_limited',
  'internal',
]);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

export const ErrorMessageSchema = z.object({
  t: z.literal('error'),
  code: ErrorCodeSchema,
  message: z.string(),
  /** True when the socket is about to be closed. */
  fatal: z.boolean(),
});

export const PongMessageSchema = z.object({
  t: z.literal('pong'),
  sentAt: z.number().int(),
  serverNow: z.number().int(),
});

export const ServerMessageSchema = z.discriminatedUnion('t', [
  WelcomeMessageSchema,
  ClaimableSeatsMessageSchema,
  SnapshotMessageSchema,
  PhaseChangedMessageSchema,
  RosterMessageSchema,
  PlayerPhotoMessageSchema,
  SettingsMessageSchema,
  GameStartedMessageSchema,
  TurnStartedMessageSchema,
  QuestionRevealedMessageSchema,
  PredictionCountMessageSchema,
  PredictionAckMessageSchema,
  LockCountdownMessageSchema,
  PredictionsLockedMessageSchema,
  RevealMessageSchema,
  JustificationMessageSchema,
  RoundLeaderboardMessageSchema,
  SuperlativesMessageSchema,
  GameEndedMessageSchema,
  GameResetMessageSchema,
  TurnSkippedMessageSchema,
  SkipOfferedMessageSchema,
  ErrorMessageSchema,
  PongMessageSchema,
]);

export type ServerMessage = z.infer<typeof ServerMessageSchema>;
export type ServerMessageType = ServerMessage['t'];
export type ServerMessageOf<T extends ServerMessageType> = Extract<ServerMessage, { t: T }>;

/* ========================================================================== */
/* Helpers                                                                     */
/* ========================================================================== */

export type ParseResult<T> = { ok: true; value: T } | { ok: false; error: string };

function parseWith<T>(schema: z.ZodType<T>, raw: unknown): ParseResult<T> {
  const text = typeof raw === 'string' ? safeJson(raw) : raw;
  if (text === undefined) return { ok: false, error: 'malformed JSON' };
  const result = schema.safeParse(text);
  if (result.success) return { ok: true, value: result.data };
  return { ok: false, error: result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') };
}

function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
}

/** Validate an inbound client frame. Accepts a JSON string or a parsed value. */
export function parseClientMessage(raw: unknown): ParseResult<ClientMessage> {
  return parseWith(ClientMessageSchema, raw);
}

/** Validate an inbound server frame. Accepts a JSON string or a parsed value. */
export function parseServerMessage(raw: unknown): ParseResult<ServerMessage> {
  return parseWith(ServerMessageSchema, raw);
}

/** Serialise an outbound frame. Typed so neither side can invent a message. */
export function encodeServerMessage(message: ServerMessage): string {
  return JSON.stringify(message);
}

export function encodeClientMessage(message: ClientMessage): string {
  return JSON.stringify(message);
}

/** Messages only the host may send. */
export const HOST_ONLY_MESSAGES: readonly ClientMessageType[] = [
  'update_settings',
  'start_game',
  'force_advance',
  'skip_turn',
  'remove_player',
  'end_game',
  'play_again',
];

/** Messages only the current turn player may send. */
export const TURN_PLAYER_ONLY_MESSAGES: readonly ClientMessageType[] = [
  'select_question',
  'submit_ranking',
  'justification_done',
];

export function isHostOnly(t: ClientMessageType): boolean {
  return HOST_ONLY_MESSAGES.includes(t);
}

export function isTurnPlayerOnly(t: ClientMessageType): boolean {
  return TURN_PLAYER_ONLY_MESSAGES.includes(t);
}
